import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, Plus, Trash2, Save, Clock, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STATUSES = ['', 'Not Picked', 'Not Interested', 'Rejected', 'Prospect', 'JD Sent', 'Sent to Client'];

export default function CCR() {
  const [positions, setPositions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [activeSession, setActiveSession] = useState(null);
  const [entries, setEntries] = useState([]);
  const [callSession, setCallSession] = useState('pre_lunch');
  const [newEntry, setNewEntry] = useState({ candidate_name: '', mobile_no: '', email: '', status_1st_call: '', status_2nd_call: '', status_3rd_call: '', remarks: '' });
  const [mySessions, setMySessions] = useState([]);

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await axios.get(`${API}/positions`, { headers: getHeaders() });
        setPositions(res.data);
      } catch (err) { console.error("./pages/CCR.jsx:", err); }
    };
    fetchPositions();
  }, []);

  useEffect(() => {
    const fetchMySessions = async () => {
      try {
        const res = await axios.get(`${API}/ccr/my-sessions?date=${selectedDate}`, { headers: getHeaders() });
        setMySessions(res.data);
      } catch (err) { console.error("./pages/CCR.jsx:", err); }
    };
    if (selectedDate) fetchMySessions();
  }, [selectedDate, activeSession]);

  const startSession = async () => {
    if (!selectedPosition) { toast.error('Select a position first'); return; }
    try {
      const res = await axios.post(`${API}/ccr`, { date: selectedDate, position_id: selectedPosition }, { headers: getHeaders() });
      setActiveSession(res.data);
      const entriesRes = await axios.get(`${API}/ccr/entries/${res.data.id}`, { headers: getHeaders() });
      setEntries(entriesRes.data);
      toast.success('Session started');
    } catch (e) { toast.error('Failed to start session'); }
  };

  const addEntry = async () => {
    if (!newEntry.candidate_name || !newEntry.mobile_no) { toast.error('Name and mobile are required'); return; }
    try {
      const res = await axios.post(`${API}/ccr/${activeSession.id}/entries`, { ...newEntry, session: callSession }, { headers: getHeaders() });
      setEntries([...entries, res.data]);
      setNewEntry({ candidate_name: '', mobile_no: '', email: '', status_1st_call: '', status_2nd_call: '', status_3rd_call: '', remarks: '' });
      toast.success('Entry added');
    } catch (e) { toast.error('Failed to add entry'); }
  };

  const updateEntry = async (entry) => {
    try {
      await axios.put(`${API}/ccr/entries/${entry.id}`, entry, { headers: getHeaders() });
      toast.success('Updated');
    } catch (e) { toast.error('Failed to update'); }
  };

  const deleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/ccr/entries/${entryId}`, { headers: getHeaders() });
      setEntries(entries.filter(e => e.id !== entryId));
      toast.success('Entry deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const loadSession = async (session) => {
    setActiveSession(session);
    try {
      const res = await axios.get(`${API}/ccr/entries/${session.id || session.ccr_id}`, { headers: getHeaders() });
      setEntries(res.data);
    } catch (err) { console.error("./pages/CCR.jsx:", err); }
  };

  const StatusSelect = ({ value, onChange, testId }) => (
    <select data-testid={testId} value={value} onChange={onChange}
      className="h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs focus:ring-1 focus:ring-blue-600">
      {STATUSES.map(s => <option key={s} value={s}>{s || '—'}</option>)}
    </select>
  );

  const preLunchEntries = entries.filter(e => e.session === 'pre_lunch');
  const postLunchEntries = entries.filter(e => e.session === 'post_lunch');

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="ccr-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Candidate Calling Report</h1>
        <p className="text-slate-600">Track your daily calling activity</p>
      </div>

      {/* Date & Position Selection */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date</Label>
            <Input data-testid="ccr-date" type="date" value={selectedDate} onChange={(e) => { setSelectedDate(e.target.value); setActiveSession(null); setEntries([]); }} className="mt-2 rounded border-blue-200" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Position</Label>
            <select data-testid="ccr-position" value={selectedPosition} onChange={(e) => setSelectedPosition(e.target.value)}
              className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
              <option value="">Select position</option>
              {positions.map(p => <option key={p.id} value={p.id}>{p.title} — {p.client_name}</option>)}
            </select>
            <div className="mt-2">
              <SearchableSelect value={selectedPosition} onChange={setSelectedPosition}
                items={positions} getLabel={(p) => p.title} getSublabel={(p) => p.client_name}
                emptyLabel="Select position" placeholder="Search position or client…" testId="ccr-position-search" />
            </div>
          </div>
          <Button data-testid="start-session-btn" onClick={startSession} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
            <Phone size={16} className="mr-2" />Start / Load Session
          </Button>
        </div>
      </div>

      {/* Existing sessions for the day */}
      {mySessions.length > 0 && !activeSession && (
        <div className="bg-white border border-blue-100 rounded shadow-sm p-6 mb-6">
          <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-3">Your Sessions for {selectedDate}</p>
          <div className="flex flex-wrap gap-3">
            {mySessions.map(s => (
              <button key={s.id} onClick={() => loadSession(s)} data-testid={`session-${s.id}`}
                className="px-4 py-3 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors text-left">
                <p className="font-semibold text-blue-900 text-sm">{s.position_title}</p>
                <p className="text-xs text-slate-500">{s.client_name} — {s.total_calls} calls</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active Session */}
      {activeSession && (
        <div>
          <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4 flex items-center justify-between">
            <div>
              <p className="font-bold text-blue-900">{activeSession.position_title} <span className="text-slate-500 font-normal text-sm">— {activeSession.client_name}</span></p>
              <p className="text-xs text-slate-500">Date: {activeSession.date}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setCallSession('pre_lunch')} data-testid="session-pre-lunch"
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${callSession === 'pre_lunch' ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 border border-slate-300'}`}>
                <Clock size={12} className="inline mr-1" />Pre Lunch
              </button>
              <button onClick={() => setCallSession('post_lunch')} data-testid="session-post-lunch"
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${callSession === 'post_lunch' ? 'bg-indigo-500 text-white' : 'bg-white text-slate-600 border border-slate-300'}`}>
                <Clock size={12} className="inline mr-1" />Post Lunch
              </button>
            </div>
          </div>

          {/* Add Entry Form */}
          <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4">
            <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-3">Add Candidate ({callSession === 'pre_lunch' ? 'Pre Lunch' : 'Post Lunch'})</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
              <Input data-testid="ccr-name" placeholder="Candidate Name" value={newEntry.candidate_name} onChange={(e) => setNewEntry({ ...newEntry, candidate_name: e.target.value })} className="rounded border-blue-200 text-sm" />
              <Input data-testid="ccr-mobile" placeholder="Mobile No." value={newEntry.mobile_no} onChange={(e) => setNewEntry({ ...newEntry, mobile_no: e.target.value })} className="rounded border-blue-200 text-sm" />
              <Input data-testid="ccr-email" type="email" placeholder="Email (optional)" value={newEntry.email} onChange={(e) => setNewEntry({ ...newEntry, email: e.target.value })} className="rounded border-blue-200 text-sm" />
              <StatusSelect value={newEntry.status_1st_call} onChange={(e) => setNewEntry({ ...newEntry, status_1st_call: e.target.value })} testId="ccr-status1" />
              <Input data-testid="ccr-remarks" placeholder="Remarks" value={newEntry.remarks} onChange={(e) => setNewEntry({ ...newEntry, remarks: e.target.value })} className="rounded border-blue-200 text-sm" />
            </div>
            <Button data-testid="add-entry-btn" onClick={addEntry} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
              <Plus size={14} className="mr-1" />Add Entry
            </Button>
          </div>

          {/* Pre-lunch entries */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded">Pre Lunch</span>
              <span className="text-sm text-slate-500">{preLunchEntries.length} calls</span>
            </div>
            <EntryTable entries={preLunchEntries} onUpdate={updateEntry} onDelete={deleteEntry} />
          </div>

          {/* Post-lunch entries */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-indigo-500 text-white text-xs font-bold px-2 py-0.5 rounded">Post Lunch</span>
              <span className="text-sm text-slate-500">{postLunchEntries.length} calls</span>
            </div>
            <EntryTable entries={postLunchEntries} onUpdate={updateEntry} onDelete={deleteEntry} />
          </div>
        </div>
      )}
    </div>
  );
}

function EntryTable({ entries, onUpdate, onDelete }) {
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const STATUSES = ['', 'Not Picked', 'Not Interested', 'Rejected', 'Prospect', 'JD Sent', 'Sent to Client'];

  const startEdit = (entry) => { setEditingId(entry.id); setEditRow({ ...entry }); };
  const saveEdit = () => { onUpdate(editRow); setEditingId(null); };

  if (entries.length === 0) return <p className="text-sm text-slate-400 py-3">No entries yet</p>;
  return (
    <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-blue-50">
            <TableHead className="font-bold text-blue-900 text-xs">#</TableHead>
            <TableHead className="font-bold text-blue-900 text-xs">Name</TableHead>
            <TableHead className="font-bold text-blue-900 text-xs">Mobile</TableHead>
            <TableHead className="font-bold text-blue-900 text-xs">Email</TableHead>
            <TableHead className="font-bold text-blue-900 text-xs">1st Call</TableHead>
            <TableHead className="font-bold text-blue-900 text-xs">2nd Call</TableHead>
            <TableHead className="font-bold text-blue-900 text-xs">3rd Call</TableHead>
            <TableHead className="font-bold text-blue-900 text-xs">Remarks</TableHead>
            <TableHead className="font-bold text-blue-900 text-xs">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry, idx) => (
            <TableRow key={entry.id} className="hover:bg-blue-50">
              <TableCell className="text-xs text-slate-500">{idx + 1}</TableCell>
              {editingId === entry.id ? (
                <>
                  <TableCell><Input value={editRow.candidate_name} onChange={(e) => setEditRow({ ...editRow, candidate_name: e.target.value })} className="h-7 text-xs rounded border-blue-200" /></TableCell>
                  <TableCell><Input value={editRow.mobile_no} onChange={(e) => setEditRow({ ...editRow, mobile_no: e.target.value })} className="h-7 text-xs rounded border-blue-200" /></TableCell>
                  <TableCell><Input value={editRow.email || ''} onChange={(e) => setEditRow({ ...editRow, email: e.target.value })} className="h-7 text-xs rounded border-blue-200" placeholder="email" /></TableCell>
                  <TableCell><select value={editRow.status_1st_call} onChange={(e) => setEditRow({ ...editRow, status_1st_call: e.target.value })} className="h-7 w-full rounded border border-slate-200 text-xs">{STATUSES.map(s => <option key={s} value={s}>{s || '—'}</option>)}</select></TableCell>
                  <TableCell><select value={editRow.status_2nd_call} onChange={(e) => setEditRow({ ...editRow, status_2nd_call: e.target.value })} className="h-7 w-full rounded border border-slate-200 text-xs">{STATUSES.map(s => <option key={s} value={s}>{s || '—'}</option>)}</select></TableCell>
                  <TableCell><select value={editRow.status_3rd_call} onChange={(e) => setEditRow({ ...editRow, status_3rd_call: e.target.value })} className="h-7 w-full rounded border border-slate-200 text-xs">{STATUSES.map(s => <option key={s} value={s}>{s || '—'}</option>)}</select></TableCell>
                  <TableCell><Input value={editRow.remarks} onChange={(e) => setEditRow({ ...editRow, remarks: e.target.value })} className="h-7 text-xs rounded border-blue-200" /></TableCell>
                  <TableCell><Button size="sm" onClick={saveEdit} className="bg-emerald-600 text-white text-xs h-7 rounded"><Save size={12} /></Button></TableCell>
                </>
              ) : (
                <>
                  <TableCell className="text-sm font-medium">{entry.candidate_name}</TableCell>
                  <TableCell className="text-sm">{entry.mobile_no}</TableCell>
                  <TableCell><StatusBadge status={entry.status_1st_call} /></TableCell>
                  <TableCell><StatusBadge status={entry.status_2nd_call} /></TableCell>
                  <TableCell><StatusBadge status={entry.status_3rd_call} /></TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-[150px] truncate">{entry.remarks}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(entry)} className="text-blue-600 h-7 w-7 p-0"><Edit2 size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(entry.id)} className="text-red-600 h-7 w-7 p-0"><Trash2 size={12} /></Button>
                    </div>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StatusBadge({ status }) {
  if (!status) return <span className="text-xs text-slate-300">—</span>;
  const colors = {
    'Not Picked': 'bg-slate-100 text-slate-700',
    'Not Interested': 'bg-red-50 text-red-700',
    'Rejected': 'bg-red-100 text-red-800',
    'Prospect': 'bg-amber-100 text-amber-800',
    'JD Sent': 'bg-blue-100 text-blue-800',
    'Sent to Client': 'bg-emerald-100 text-emerald-800',
  };
  return <span className={`px-1.5 py-0.5 text-xs font-bold rounded ${colors[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
}

function Edit2Icon(props) { return <Edit2 {...props} />; }
